import React from 'react';
import { EventLink, LinkType, LinkVisibility, LINK_TYPE_LABELS, LINK_TYPE_ORDER, LINK_VISIBILITY_LABELS } from '@radevents/shared';

interface Props {
  links: EventLink[];
  onChange: (links: EventLink[]) => void;
  /** Im öffentlichen Vorschlagsformular gibt es keine internen Links — dann Auswahl ausblenden. */
  allowInternal?: boolean;
  /** URLs, die im Review als „neu" markiert werden (Links, die der Vorschlag ergänzt). */
  newUrls?: Set<string>;
  inputStyle?: React.CSSProperties;
}

export const emptyLink = (visibility: LinkVisibility = 'PUBLIC'): EventLink => ({
  url: '',
  type: 'OTHER',
  visibility,
  label: '',
});

/** Entfernt leere Zeilen und trimmt — das, was ans Backend geht. */
export function cleanLinks(links: EventLink[]): EventLink[] {
  return links
    .map(l => ({ ...l, url: l.url.trim(), label: l.label?.trim() || undefined }))
    .filter(l => l.url.length > 0);
}

/**
 * Beliebig viele Links pro Event, je mit Typ und Sichtbarkeit. Ersetzt die frühere
 * „eine URL pro Zeile"-Textarea.
 */
export default function EventLinksEditor({ links, onChange, allowInternal = true, newUrls, inputStyle }: Props) {
  const update = (idx: number, patch: Partial<EventLink>) =>
    onChange(links.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  const remove = (idx: number) => onChange(links.filter((_, i) => i !== idx));

  const add = () => onChange([...links, emptyLink(allowInternal ? 'INTERNAL' : 'PUBLIC')]);

  return (
    <div className="flex flex-col gap-2">
      {/* Die URL bekommt eine eigene volle Zeile — der Editor steht auch in schmalen Spalten
          (Review-Ansicht), wo URL + Selects nebeneinander die URL unlesbar quetschen würden. */}
      {links.map((link, idx) => (
        <div
          key={idx}
          className="flex flex-col gap-2 p-2 rounded-lg"
          style={{ border: '1px solid var(--surface-border)' }}
        >
          <div className="flex items-center gap-2">
            {newUrls?.has(link.url.trim()) && (
              <span
                className="shrink-0 px-1.5 py-0.5 rounded text-xs font-bold"
                style={{ background: 'var(--badge-green-bg, #d1fae5)', color: 'var(--badge-green-text, #065f46)' }}
              >
                neu
              </span>
            )}
            <input
              value={link.url}
              onChange={e => update(idx, { url: e.target.value })}
              placeholder="https://..."
              aria-label="URL"
              className="flex-1 min-w-0 px-3 py-2 rounded-lg text-sm"
              style={inputStyle}
            />
            <button
              type="button"
              onClick={() => remove(idx)}
              aria-label="Link entfernen"
              className="px-3 py-2 rounded-lg text-sm shrink-0"
              style={{ color: 'var(--text-muted)', border: '1px solid var(--surface-border)' }}
            >
              ✕
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              value={link.type}
              onChange={e => update(idx, { type: e.target.value as LinkType })}
              aria-label="Link-Typ"
              className="flex-1 min-w-[8rem] px-3 py-2 rounded-lg text-sm"
              style={inputStyle}
            >
              {LINK_TYPE_ORDER.map(t => (
                <option key={t} value={t}>{LINK_TYPE_LABELS[t]}</option>
              ))}
            </select>
            {allowInternal && (
              <select
                value={link.visibility}
                onChange={e => update(idx, { visibility: e.target.value as LinkVisibility })}
                aria-label="Sichtbarkeit"
                className="flex-1 min-w-[7rem] px-3 py-2 rounded-lg text-sm"
                style={inputStyle}
              >
                {(['PUBLIC', 'INTERNAL'] as LinkVisibility[]).map(v => (
                  <option key={v} value={v}>{LINK_VISIBILITY_LABELS[v]}</option>
                ))}
              </select>
            )}
            <input
              value={link.label ?? ''}
              onChange={e => update(idx, { label: e.target.value })}
              placeholder="Beschriftung (optional)"
              aria-label="Beschriftung"
              className="flex-1 min-w-[8rem] px-3 py-2 rounded-lg text-sm"
              style={inputStyle}
            />
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        className="self-start text-sm font-semibold hover:underline"
        style={{ color: 'var(--links-hinzufuegen, var(--accent-warm))' }}
      >
        + Link hinzufügen
      </button>
    </div>
  );
}
